import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAboutPage = () => {
  const { getAuthHeaders } = useApp();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/about`);
      setContent(data);
    } catch (error) {
      toast.error('Failed to fetch content');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.put(`${BACKEND_URL}/api/about`, content, {
        headers: getAuthHeaders(),
      });
      toast.success('About content updated!');
    } catch (error) {
      toast.error('Failed to update content');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPG, PNG, GIF, or WebP images.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/upload/image`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
      });

      setContent({ ...content, image_url: response.data.url });
      toast.success('Image uploaded! Click Save to apply changes.');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const removeImage = () => {
    setContent({ ...content, image_url: '' });
  };

  if (!content) return <AdminLayout><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div data-testid="admin-about-page">
        <h1 className="text-4xl font-serif mb-8">About Content</h1>

        <form onSubmit={handleSubmit} className="glass p-8 space-y-6">
          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Title</label>
            <div className="grid grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Romanian"
                value={content.title.ro}
                onChange={(e) => setContent({ ...content, title: { ...content.title, ro: e.target.value } })}
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="English"
                value={content.title.en}
                onChange={(e) => setContent({ ...content, title: { ...content.title, en: e.target.value } })}
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="French"
                value={content.title.fr}
                onChange={(e) => setContent({ ...content, title: { ...content.title, fr: e.target.value } })}
                className="bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">Content</label>
            <div className="grid grid-cols-1 gap-4">
              <textarea
                placeholder="Romanian"
                value={content.content.ro}
                onChange={(e) => setContent({ ...content, content: { ...content.content, ro: e.target.value } })}
                rows={10}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
              <textarea
                placeholder="English"
                value={content.content.en}
                onChange={(e) => setContent({ ...content, content: { ...content.content, en: e.target.value } })}
                rows={10}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
              <textarea
                placeholder="French"
                value={content.content.fr}
                onChange={(e) => setContent({ ...content, content: { ...content.content, fr: e.target.value } })}
                rows={10}
                className="bg-transparent border border-white/20 p-3 text-sm focus:outline-none focus:border-primary resize-none"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-primary mb-2 block">About Page Image</label>
            
            {/* Upload from file */}
            <div className="mb-4 p-4 bg-slate-900/50 border border-white/10 rounded">
              <label className="text-xs text-muted-foreground mb-2 block">Upload Image File</label>
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                  className="flex-1 text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                />
                {uploadingImage && <span className="text-xs text-muted-foreground">Uploading...</span>}
              </div>
              <p className="text-xs text-muted-foreground mt-2">Max 5MB • JPG, PNG, GIF, WebP</p>
            </div>

            {/* Or add by URL */}
            <div className="mb-4">
              <label className="text-xs text-muted-foreground mb-2 block">Or Enter Image URL</label>
              <input
                type="url"
                placeholder="https://example.com/image.jpg"
                value={content.image_url}
                onChange={(e) => setContent({ ...content, image_url: e.target.value })}
                className="w-full bg-transparent border-b border-white/20 py-2 text-sm focus:outline-none focus:border-primary"
              />
            </div>

            {/* Image preview */}
            {content.image_url && (
              <div className="relative inline-block">
                <img src={content.image_url} alt="Preview" className="w-full h-48 object-cover rounded" />
                <button
                  type="button"
                  onClick={removeImage}
                  className="absolute top-2 right-2 bg-red-500 p-1 rounded hover:bg-red-600 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            )}
          </div>

          <Button type="submit" disabled={loading} data-testid="save-about-button" className="w-full bg-primary text-primary-foreground">
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminAboutPage;